import React, { useCallback, useEffect, useRef } from 'react';
import { useClicker } from '@hook/common/useEvent';
import styled from 'styled-components';
import IconBlock from '@component/common/block/icon-block';
import { ETheme } from '@datatype/type';
import * as color from '@constant/color';
import * as size from '@constant/size';
import * as font from '@constant/font';

interface IProps {
  value: string;
  placeholder?: string;
  name?: string;
  theme?: ETheme;
  size?: 'small' | 'medium' | 'large';
  width?: string;
  height?: string;
  readOnly?: boolean;
  disabled?: boolean;
  onChange?: (value: string) => void;
  handleSearch?: (value: string) => void;
  autoSearch?: boolean;
  debounceDelay?: number;
  handleClick?: () => void;
}

const createDesign = (componentSize: IProps['size'], theme: ETheme, width?: string, height?: string) => {
  const createSize = () => {
    switch (componentSize) {
      case 'small':
        return {
          minHeight: size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT,
          paddingX: size.DESIGN_SYSTEM_BUTTON_SMALL_PADDING_X,
          font: font.FONT_SMALL_400,
        };
      case 'large':
        return {
          minHeight: size.DESIGN_SYSTEM_BUTTON_LARGE_HEIGHT,
          paddingX: size.DESIGN_SYSTEM_BUTTON_LARGE_PADDING_X,
          font: font.FONT_LARGE_400,
        };
      default:
        return {
          minHeight: size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT,
          paddingX: size.DESIGN_SYSTEM_BUTTON_MEDIUM_PADDING_X,
          font: font.FONT_MEDIUM_400,
        };
    }
  };
  const createTheme = () => ({
    accent: theme === ETheme.CYAN ? color.CYAN_2 : theme === ETheme.BROWN ? color.BROWN_2 : color.BLACK_2,
    background: color.WHITE_1,
    border: color.GRAY_2,
    text: color.BLACK_2,
  });
  const createEvent = () => ({ cursor: 'pointer' });
  return {
    ...createSize(),
    ...createTheme(),
    event: createEvent(),
    width,
    height,
    gap: size.MARGIN_1,
    borderWidth: size.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH,
    borderRadius: size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS,
    iconSize: componentSize ?? 'medium',
  };
};

type TDesign = { $design: ReturnType<typeof createDesign> };
const Wrapper = styled.form<TDesign>`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-width: 0;
  width: ${(props) => props.$design.width || 'fit-content'};
  height: ${(props) => props.$design.height || 'auto'};
  min-height: ${(props) => props.$design.minHeight};
  padding: 0 ${(props) => props.$design.paddingX};
  gap: ${(props) => props.$design.gap};
  border: ${(props) => props.$design.borderWidth} solid ${(props) => props.$design.border};
  border-radius: ${(props) => props.$design.borderRadius};
  background: ${(props) => props.$design.background};
  &:focus-within {
    border-color: ${(props) => props.$design.accent};
  }
`;
const Input = styled.input<TDesign>`
  border: 0;
  outline: none;
  background: transparent;
  min-width: 0;
  width: 100%;
  flex: 1;
  font: ${(props) => props.$design.font};
  color: ${(props) => props.$design.text};
  &::placeholder {
    font: ${(props) => props.$design.font};
  }
  &[readonly] {
    cursor: ${(props) => props.$design.event.cursor};
  }
`;
const SearchButton = styled.button<TDesign>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: ${(props) => props.$design.event.cursor};
  &:disabled {
    cursor: auto;
  }
`;

const SearchInput: React.FC<IProps> = ({
  value,
  placeholder,
  name,
  theme = ETheme.BROWN,
  size = 'medium',
  width,
  height,
  readOnly,
  disabled,
  onChange,
  handleSearch,
  handleClick,
  autoSearch = false,
  debounceDelay = 400,
}) => {
  const design = createDesign(size, theme, width, height);
  // 한글 입력 중 Enter는 쓰던 글자를 확정하는 데도 쓰인다.
  // 이 Enter가 검색 제출까지 실행하지 않도록 composition 이벤트로 글자 조합 상태를 기록한다.
  // 자동 검색은 별개로, 조합 중에도 입력이 멈추면 실행한다. 미완성 자모 필터는 호출부에서 처리한다.
  const composingRef = useRef(false);
  // 숫자 자체가 아닌 예약 당시 값과 현재 값의 일치 여부로 검색 예약의 유효성을 구분한다.
  // ref 변경은 리렌더링을 발생시키지 않는다.
  const searchVersionRef = useRef(0);
  const cancelSearch = useCallback(() => {
    // 타이머를 제거하는 대신 번호를 바꿔 이전 예약 콜백이 요청을 보내지 못하게 한다.
    ++searchVersionRef.current;
  }, []);
  const debouncedSearch = useClicker(
    (keyword: string, version: number) => {
      // version은 예약할 때 전달한 번호다. 입력 변경·즉시 검색·이탈로 무효화됐으면 무시한다.
      if (version === searchVersionRef.current) handleSearch?.(keyword);
    },
    debounceDelay,
    'debounce',
  );

  // 검색 가능 조건·지연 시간이 바뀌거나 언마운트되면 이전 예약을 무효화한다.
  useEffect(() => {
    cancelSearch();
    return cancelSearch;
  }, [disabled, readOnly, autoSearch, debounceDelay, cancelSearch]);

  const scheduleSearch = (keyword: string) => {
    cancelSearch();
    if (autoSearch && !disabled && !readOnly && keyword.trim()) {
      // 마지막 입력 후 debounceDelay가 지나면, 이 시점의 번호와 검색어로 실행한다.
      debouncedSearch?.(keyword, searchVersionRef.current);
    }
  };

  return (
    <Wrapper
      $design={design}
      role='search'
      onBlur={(event) => {
        // 검색 버튼으로 포커스를 옮기는 경우는 유지하고, 검색창 밖으로 나갈 때 무효화한다.
        if (!event.currentTarget.contains(event.relatedTarget)) cancelSearch();
      }}
      onSubmit={(event) => {
        event.preventDefault();
        // Enter·돋보기로 즉시 검색한 뒤 예약 검색이 중복 실행되는 것을 막는다.
        cancelSearch();
        // 글자를 확정하려고 누른 Enter라면 검색을 제출하지 않는다.
        if (!disabled && !composingRef.current) handleSearch?.(value);
      }}>
      <Input
        $design={design}
        type='search'
        aria-label={placeholder || '검색'}
        name={name}
        value={value}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={disabled}
        onChange={(event) => {
          onChange?.(event.target.value);
          scheduleSearch(event.target.value);
        }}
        onCompositionStart={() => {
          // 조합 시작 시 이전 예약은 무효화하고, 이후 onChange의 마지막 입력으로 다시 예약한다.
          composingRef.current = true;
          cancelSearch();
        }}
        onCompositionEnd={(event) => {
          // 조합 완료 후 완성된 문자열로 자동 검색을 예약한다.
          composingRef.current = false;
          scheduleSearch(event.currentTarget.value);
        }}
        onClick={handleClick}
      />
      <SearchButton $design={design} type='submit' aria-label='검색' disabled={disabled} onClick={handleClick}>
        <IconBlock icon='search' design={{ icon: { size: design.iconSize, color: design.accent } }} />
      </SearchButton>
    </Wrapper>
  );
};
export default SearchInput;
